import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { broadcastAccountData } from 'src/composables/app/system/useBroadcastChannel'
import { log } from 'src/services/app/log.service'
import { notifyMsgs, notifyService } from 'src/services/app/notify.service'
import { sessionStorageService } from 'src/services/app/storage.service'
import { authService } from 'src/services/api/auth.service'
import { STORAGE_KEYS } from 'src/services/consts/api.const'
import { MUTATION_KEYS, QUERY_KEYS } from 'src/services/consts/queries.const'
import { wrapMutationAsync } from 'src/services/helpers/util.helper'

/**
 * Complete-onboarding mutation for the app-level session gate.
 *
 * Persists via `authService.updateAccount` (PATCH /me). Accepts optional
 * profile fields (e.g. `{ firstName, lastName }`) merged with
 * `{ onboardingDone: true }`, replaces the ACCOUNT_DATA query cache, then
 * broadcasts for cross-tab sync.
 *
 * Marks the trial banner dismissed for this browser session so a brand-new
 * signup does not see it on first entry. The next session still shows it.
 *
 * No success toast — the gate closing is the feedback. Failures do notify:
 * without it the panel silently stays open, and the HTTP interceptor only
 * toasts 403 / session-expiry, so nothing else would tell the user.
 *
 * Consumed via `useAccountActionsStore` — components never call this
 * composable directly (project mutation convention).
 */
export function useCompleteOnboardingMutation() {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationKey: [MUTATION_KEYS.COMPLETE_ONBOARDING],
    mutationFn: (payload = {}) => authService.updateAccount({ ...payload, onboardingDone: true }),

    onSuccess: (updatedAccount) => {
      if (!updatedAccount) return

      sessionStorageService.set(STORAGE_KEYS.TRIAL_BANNER_DISMISSED, true)
      queryClient.setQueryData([QUERY_KEYS.ACCOUNT_DATA], updatedAccount)
      broadcastAccountData(updatedAccount)
    },

    onError: (error) => {
      log.error('[Account] completeOnboarding failed', error)
      notifyService.error(notifyMsgs.error.completeOnboarding)
    },
  })

  return {
    completeOnboarding: wrapMutationAsync(mutation.mutateAsync),
    isCompletingOnboarding: mutation.isPending,
    completeOnboardingError: mutation.error,
  }
}
